import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SubObjectiveItem } from '~src/store/okrs/interfaces';
import { EditSection, IconWrapper, Title } from '~src/modules/okrs/manageObjectives/edit/keyResult/keyResultDetails.sc';
import SvgIcon from '~src/shared/SvgIcon';
import { DetailsHeader, DetailsWrapper } from '~src/modules/okrs/manageObjectives/edit/shared.sc';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import { ApiReduxResp } from '~src/services/apiMiddleware';
import { useNotification } from '~src/services/hooks/useNotification';
import { translateContent } from '~src/services/translationServices';
import SelectorHandler from '~src/modules/okrs/manageObjectives/shared/SelectorHandler';
import ProgressBar from '~src/shared/ProgressBar';

import EditSubobjectiveForm from './EditSubobjectiveForm';

interface OwnProps {
  subobjective: SubObjectiveItem;

  subobjectives: SubObjectiveItem[];

  detachSubobjective(subobjectiveId: string): ApiReduxResp;

  attachSubobjective(subobjectiveId: string, weighting: number | null): ApiReduxResp;

  close(): void;
}

const EditSubobjectiveDetails: FC<OwnProps> = props => {
  const [showDelConfirm, setShowDelConfirm] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const [t] = useTranslation();
  const { notificationsFromAPIErrors } = useNotification();

  const onDelClick = () => {
    setShowDelConfirm(true);
  };

  const hideDelModal = () => {
    setShowDelConfirm(false);
  };

  const deleteSubobjective = () => {
    setIsLoading(true);
    props
      .detachSubobjective(props.subobjective.id)
      .then(rsp => {
        if (rsp.error) {
          notificationsFromAPIErrors(rsp.error.response.data, false, 'x.okr.error');
        }
      })
      .finally(() => {
        setIsLoading(false);
        props.close();
      });
  };

  const changeWeight = (subobjectiveId: string, weighting: number | null) => {
    setIsLoading(true);
    props
      .attachSubobjective(subobjectiveId, weighting)
      .then(rsp => {
        if (rsp.error) {
          notificationsFromAPIErrors(rsp.error.response.data, false, 'x.okr.error');
          return;
        }
        props.close();
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  return (
    <>
      <ActionConfirmDialog
        t={t}
        show={showDelConfirm}
        close
        onHide={hideDelModal}
        actionTitleKey={'x.okr.sub_objective.delete.modal.title'}
        actionContentKey={null}
        actionCall={deleteSubobjective}
        showDeletion
        loadingAction={isLoading}
      />
      <DetailsWrapper>
        <DetailsHeader>
          <IconWrapper onClick={props.close}>
            <SvgIcon name="iconCross" />
          </IconWrapper>
          <EditSection>
            <IconWrapper onClick={onDelClick}>
              <SvgIcon name="iconBin" />
            </IconWrapper>
          </EditSection>
        </DetailsHeader>
        <SelectorHandler iconName="iconClipBoard">
          {() => <Title>{translateContent(props.subobjective.name)}</Title>}
        </SelectorHandler>
        <SelectorHandler iconName="iconEmpty">
          {() => (
            <>
              <span data-translation="x.okrs.progress.indicator">
                {t('x.okrs.progress.indicator', { progress: props.subobjective.progress })}
              </span>
              <ProgressBar height={5} width="100%" staticProgress progress={props.subobjective.progress} />
              <EditSubobjectiveForm
                changeWeight={changeWeight}
                subobjective={props.subobjective}
                hide={props.close}
              />
            </>
          )}
        </SelectorHandler>
      </DetailsWrapper>
    </>
  );
};

export default EditSubobjectiveDetails;
